<template>
  <router-link
    :to="campaignRoute"
    :class="classes"
  >
    <c-card
      class="card"
      :class="{ '-square-card': square }"
      data-cy="campaign-card"
    >
      <campaign-tag
        class="tag"
        :is-cashback="isCashback && !campaign.value"
        :discount-type="tagSymbol === '%' ? 'percentage' : 'currency'"
        :show-discont-tag="!isNextToYou"
        :discount-value="+tagValue"
        :discount-symbol="tagSymbol"
        :is-exclusive="isExclusive"
        :from-now-discount-value="campaign.discounted_value"
        :is-from-now="tagSymbol === 'de-por'"
        :is-award="campaign.event === 'award'"
        :is-engagement="campaign.event === 'engagement'"
      />

      <campaign-tag
        v-if="!isNextToYou && isCashback && campaign.value"
        bottom
        class="tag"
        :is-cashback="isCashback"
        :discount-type="cashbackSymbol === '%' ? 'percentage' : 'currency'"
        :discount-value="+cashbackValue"
        :discount-symbol="cashbackSymbol"
      />

      <c-image
        class="image"
        :src="image"
        :alt="campaign.title"
        placeholder="~@/assets/img/campaign-placeholder.svg"
        is-background
      />

      <template v-if="square">
        <h4
          v-if="!isNextToYou"
          class="partner"
        >
          {{ campaign.title }}
        </h4>
        <div
          v-if="isNextToYou"
          class="distance"
        >
          <p class="partner">
            {{ adjustedPartnerName }}
          </p>
          <div>
            <k-icon
              size="10"
              icon="map-marker"
            />
            {{ campaign.distance }} km
          </div>
        </div>
      </template>

      <template v-else>
        <div
          class="description"
          :class="isNextToYou ? '-next-to-you' : ''"
        >
          <div class="partner">
            <h4 class="name">
              {{ adjustedPartnerName }}
            </h4>
          </div>

          <p class="title">
            {{ campaign.title }}
          </p>
        </div>

        <c-button
          v-if="hasFavorite"
          size="md"
          class="favorite"
          data-cy="campaign-card-favorite-button"
          :icon="campaign.is_favorited ? 'filled-bookmark' : 'bookmark'"
          :class="{ '-favorited': campaign.is_favorited }"
          @click.stop="handleAnalytics"
        />

        <div
          v-if="isNextToYou"
          class="distance -card"
        >
          <div>
            <k-icon
              size="10"
              icon="map-marker"
            />
            {{ campaign.distance }} km
          </div>
        </div>
        <p
          v-if="expiry && !search"
          class="expiry"
        >
          <k-icon
            size="15"
            icon="alarm-clock"
          />
          Expira em {{ expiry }}
        </p>
      </template>
    </c-card>
  </router-link>
</template>

<script>
import CampaignTag from '@components/Campaign/CampaignTag'
import isIntegerOrFloat from '@helpers/isIntegerOrFloat'
import oneDecimalPlaceFormat from '@helpers/oneDecimalPlaceFormat'
import { formatDate, getDiff, DAY } from '@helpers/time'
import { KIcon } from '@/components/KaledoComponents'

require('@assets/icons/filled-heart')
require('@assets/icons/heart2')
require('@assets/icons/filled-bookmark')
require('@assets/icons/bookmark')

export default {
  components: { CampaignTag, KIcon },

  props: {
    square: Boolean,
    search: Boolean,
    campaign: Object,
    hasFavorite: Boolean,
    isCashback: Boolean,
    cashbackValue: [Boolean, String, Number]
  },

  computed: {
    classes () {
      const classes = [
        'campaign-card', {
          '-square': !!this.square,
          '-search': !!this.search,
          '-exclusive': !!this.isExclusive,
          '-cashback': !!this.isCashback
        }]
      return classes
    },
    partner () {
      return (this.campaign.title && this.campaign.partner.name) || this.campaign.name
    },
    image () {
      if (this.campaign.isNextToYou) return this.campaign.logo
      return this.campaign.partner && this.campaign.partner.logo
    },
    isExclusive () {
      if (this.campaign.isNextToYou) return true
      return !!(this.campaign.partner && this.campaign.partner.is_exclusive)
    },

    tagSymbol () {
      return this.isCashback && !this.value ? this.cashbackSymbol : this.campaign.symbol
    },

    tagValue () {
      const value = this.isCashback && !this.value ? this.cashbackValue : this.campaign.value
      return (typeof value === 'number') && !isIntegerOrFloat(value) ? oneDecimalPlaceFormat(value) : value
    },

    campaignRoute () {
      const { event, slug, partner } = this.campaign
      switch (event) {
      case 'award':
        return {
          name: 'Detalhes do premio',
          params: { partnerSlug: partner.slug }
        }

      case 'payment':
        return {
          name: 'Detalhes da campanha para pagamento',
          params: { campaignSlug: slug }
        }

      default:
        return {
          name: 'Detalhes da campanha',
          query: this.$route.query,
          params: { campaignSlug: slug }
        }
      }
    },

    cashbackSymbol () {
      if (this.campaign && this.campaign.isNextToYou) return false
      return this.campaign.eventData.cashback_symbol
    },

    isNextToYou () {
      const value = this.campaign.isNextToYou ? this.campaign.isNextToYou : false
      return value
    },

    adjustedPartnerName () {
      if (this.partner.length > 13) return `${this.partner.substr(0, 13)}...`
      return this.partner
    },

    // localTest () {
    //   const isNotProduction = process.env.NODE_ENV !== 'production'
    //   const isNotStaging = process.env.NODE_ENV !== 'staging'
    //   if (isNotProduction && isNotStaging) return `(${this.campaign.event})`
    //   return ''
    // },

    expiry () {
      if (!this.campaign.end_date) { return }

      const expirationDate = typeof this.campaign.end_date === 'number'
        ? this.campaign.end_date * 1000
        : (this.campaign.end_date || '').replace(/-/g, '/')

      const days = getDiff(expirationDate, new Date(), DAY)

      const suffix = ' dia' + (days === 1 ? '' : 's')

      if (days > 0 && days <= 3) { return days + suffix } else { return formatDate(expirationDate) }
    }
  },

  methods: {
    handleAnalytics (e) {
      e.preventDefault()
      this.$emit('favorite')

      window.dataLayer.push({
        event: 'Favoritos',
        nome_parceiro: this.partner,
        slug_oferta: this.campaign.slug,
        titulo_oferta: this.campaign.title
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/components/Campaign/scss/campaign-card.scss';
</style>



// WEBPACK FOOTER //
// src/components/Campaign/CampaignCard.vue